import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/shared/api';
import { isTerminalAriOperation } from './ariOperationStatus';
import {
  readTrackedAriOperations,
  removeTrackedAriOperation,
  subscribeToTrackedAriOperations,
} from './ariOperationStorage';
import { invalidateChannex } from './refreshChannex';

export function useAriOperationStatuses(propertyId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  const [tracked, setTracked] = useState(() => readTrackedAriOperations(propertyId));
  const invalidated = useRef(new Set<string>());
  const ids = useMemo(() => tracked.map((entry) => entry.id), [tracked]);

  useEffect(() => {
    const refresh = () => setTracked(readTrackedAriOperations(propertyId));
    refresh();
    return subscribeToTrackedAriOperations(propertyId, refresh);
  }, [propertyId]);

  const query = useQuery({
    queryKey: ['channex', 'ariOperations', propertyId, ids.join(',')],
    queryFn: () => api.channex.ariOperationStatus(propertyId, ids),
    enabled: ids.length > 0,
    refetchInterval: (state) => {
      const items = state.state.data?.items ?? [];
      const hasUnknown = items.length < ids.length;
      return hasUnknown || items.some((item) => !isTerminalAriOperation(item.status))
        ? 2_000
        : false;
    },
  });

  useEffect(() => {
    const completed = (query.data?.items ?? []).filter((item) =>
      isTerminalAriOperation(item.status),
    );
    if (!completed.some((item) => !invalidated.current.has(item.operationId))) return;
    completed.forEach((item) => invalidated.current.add(item.operationId));
    void invalidateChannex(queryClient);
  }, [query.data, queryClient]);

  return {
    tracked,
    items: query.data?.items ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    dismiss: (id: string) => removeTrackedAriOperation(propertyId, id),
  };
}
